import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    TextField,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    MenuItem,
    Select, 
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Print as PrintIcon,
    PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../config/api';
import { QRCodeCanvas } from 'qrcode.react';

const InvitationDesign = () => {
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedInvitation, setSelectedInvitation] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        background_color: '#ffffff',
        text_color: '#000000',
        font_family: 'Arial',
        font_size: '16',
        border_color: '#000000',
        border_width: '1',
        border_style: 'solid',
        border_radius: '20',
        padding: '20',
        alignment: 'center',
    });

    const { codeHash } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const previewRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            await fetchInvitations();
            await fetchEventDetails();
        };
        load();
    }, [codeHash]);

    const fetchEventDetails = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.events.getByCode(codeHash), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Etkinlik bulunamadı');
            setEvent(data);
        } catch (error) {
            toast.error(error.message);
        }
    };

    const fetchInvitations = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.invitations.getAll(codeHash), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            const data = await response.json();
            if (response.ok) {
                setInvitations(data);
            }
        } catch (error) {
            toast.error('Davetiyeler yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleDialogOpen = (invitation = null) => {
        if (invitation) {
            setEditMode(true);
            setSelectedInvitation(invitation);
            setFormData({
                title: invitation.title,
                content: invitation.content,
                background_color: invitation.background_color,
                text_color: invitation.text_color,
                font_family: invitation.font_family,
                font_size: invitation.font_size,
                border_color: invitation.border_color,
                border_width: invitation.border_width,
                border_style: invitation.border_style,
                border_radius: invitation.border_radius,
                padding: invitation.padding,
                alignment: invitation.alignment,
            });
        } else {
            setEditMode(false);
            setSelectedInvitation(null);
            setFormData({
                title: event?.name || '',
                content: event?.description || '',
                background_color: '#ffffff',
                text_color: '#000000',
                font_family: 'Arial',
                font_size: '16',
                border_color: '#000000',
                border_width: '1',
                border_style: 'solid',
                border_radius: '20',
                padding: '20',
                alignment: 'center',
            });
        }
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setEditMode(false);
        setSelectedInvitation(null);
    };

    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    };

    const handleFormChange = useCallback(
        debounce((name, value) => {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }, 300),
        []
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        handleFormChange(name, value);
    };

    const handleSubmit = async () => {
        try {
            const url = editMode
                ? API_ENDPOINTS.invitations.update(codeHash, selectedInvitation.id)
                : API_ENDPOINTS.invitations.create(codeHash);

            const updatedFormData = {
                ...formData,
                font_family: formData.font_family || 'Arial',
                border_style: formData.border_style || 'solid',
                alignment: formData.alignment || 'center'
            };

            const response = await fetch(url, {
                method: editMode ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
                body: JSON.stringify(updatedFormData),
            });

            if (!response.ok) {
                throw new Error('İşlem başarısız oldu');
            }

            toast.success(editMode ? 'Davetiye güncellendi' : 'Davetiye oluşturuldu');
            handleDialogClose();
            fetchInvitations();
        } catch (error) {
            toast.error(error.message);
        }
    };

    useEffect(() => {
        const style = document.createElement('style');
        style.type = 'text/css';
        style.innerHTML = `
            @media print {
                @page {
                    size: A4 portrait;
                    margin: 0;
                }

                * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                }

                html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    background: white !important;
                    height: 100% !important;
                    width: 100% !important;
                    overflow: visible !important;
                }

                /* Tüm içeriği gizle */
                body * {
                    display: none !important;
                }

                /* Sadece davetiye önizlemesini göster */
                #invitation-preview, #invitation-preview * {
                    display: block !important;
                    visibility: visible !important;
                    color: inherit !important;
                    background-color: inherit !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                    opacity: 1 !important;
                }

                #invitation-preview {
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 210mm !important;
                    height: 297mm !important;
                    padding: 20mm !important;
                    margin: 0 !important;
                    box-sizing: border-box !important;
                    z-index: 9999 !important;
                    page-break-inside: avoid !important;
                    page-break-after: avoid !important;
                    page-break-before: avoid !important;
                    overflow: visible !important;
                    box-shadow: none !important;
                    border: none !important;
                    transform: none !important;
                    background-color: white !important;
                }
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);


    const handleDelete = async (id) => {
        if (window.confirm('Bu davetiyeyi silmek istediğinizden emin misiniz?')) {
            try {
                const response = await fetch(API_ENDPOINTS.invitations.delete(codeHash, id), {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                });

                if (!response.ok) {
                    throw new Error('Silme işlemi başarısız oldu');
                }

                toast.success('Davetiye silindi');
                fetchInvitations();
            } catch (error) {
                toast.error(error.message);
            }
        }
    };

    // QR kodunu img olarak almak için fonksiyon
    const getQrDataUrl = () => {
        const canvas = document.querySelector('#invitation-preview canvas');
        if (canvas) {
            return canvas.toDataURL('image/png');
        }
        return '';
    };

    const renderPreview = () => {
        return (
            <Box
                id="invitation-preview"
                ref={previewRef}
                sx={{
                    p: 3,
                    backgroundColor: formData.background_color,
                    color: formData.text_color,
                    fontFamily: formData.font_family,
                    fontSize: `${formData.font_size}px`,
                    minHeight: '200px',
                    border: `${formData.border_width}px ${formData.border_style} ${formData.border_color}`,
                    borderRadius: `${formData.border_radius}px`,
                    padding: `${formData.padding}px`,
                    textAlign: formData.alignment,
                    mb: 2,
                }}
            >
                <Typography variant="h5" gutterBottom style={{ fontFamily: formData.font_family, color: formData.text_color }}>
                    {formData.title}
                </Typography>
                <Typography style={{ whiteSpace: 'pre-wrap', fontFamily: formData.font_family, color: formData.text_color, fontSize: `${formData.font_size}px` }}>
                    {formData.content}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pageBreakInside: 'avoid' }}>
                    <QRCodeCanvas
                        value={`${window.location.origin}/upload/${codeHash}`}
                        size={200}
                        level="H"
                        includeMargin={true}
                        bgColor="#ffffff"
                        fgColor="#000000"
                        style={{
                            display: 'block',
                            maxWidth: '100%',
                            pageBreakInside: 'avoid',
                            backgroundColor: '#ffffff',
                            border: '1px solid #f0f0f0',
                            borderRadius: '4px',
                            padding: '10px',
                            margin: '10px auto',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact',
                            colorAdjust: 'exact'
                        }}
                    />
                </Box>
                <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', color: 'gray', fontFamily: 'Arial' }}>
                    Anı SAKLA
                </Typography>
                <Typography sx={{ textAlign: 'center', fontSize: '14px', color: 'gray', fontFamily: 'Arial' }}>
                anisakla.com
                </Typography>
            </Box>
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    const handleOpenPrintPreview = () => {
        // Ayarları localStorage'a kaydet
        localStorage.setItem('invitationPreviewData', JSON.stringify(formData));
        window.open('/invitation-print-preview/'+ codeHash, '_blank');
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`/event/${codeHash}`)}
                            >
                                Etkinliğe Geri Dön
                            </Button>
                            <Typography variant="h5">Davetiye Tasarımları</Typography>
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleDialogOpen()}
                            startIcon={<SaveIcon />}
                        >
                            Yeni Davetiye Oluştur
                        </Button>
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Başlık</TableCell>
                                    <TableCell>İçerik</TableCell>
                                    <TableCell>Arka Plan</TableCell>
                                    <TableCell>Yazı Rengi</TableCell>
                                    <TableCell>Font</TableCell>
                                    <TableCell>Kenarlık</TableCell>
                                    <TableCell>Hizalama</TableCell>
                                    <TableCell align="right">İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invitations.map((invitation) => (
                                    <TableRow key={invitation.id}>
                                        <TableCell>{invitation.title}</TableCell>
                                        <TableCell>{invitation.content.substring(0, 50)}...</TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    backgroundColor: invitation.background_color,
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    backgroundColor: invitation.text_color,
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{invitation.font_family}</TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    width: 'auto',
                                                    height: 20,
                                                    border: `${invitation.border_width}px ${invitation.border_style} ${invitation.border_color}`,
                                                    borderRadius: `${invitation.border_radius}px`,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{invitation.alignment.charAt(0).toUpperCase() + invitation.alignment.slice(1)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDialogOpen(invitation)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(invitation.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box> 

            <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth id="invitation-preview-modal">
                <DialogTitle>
                    {editMode ? 'Davetiye Düzenle' : 'Yeni Davetiye Oluştur'}
                    <IconButton
                        aria-label="close"
                        onClick={handleDialogClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Başlık"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                sx={{ mb: 2, display: 'none' }}
                            />
                            <TextField
                                fullWidth
                                label="İçerik"
                                name="content"
                                multiline
                                rows={4}
                                value={formData.content}
                                onChange={handleInputChange}
                                sx={{ mb: 2, display: 'none' }}
                            />
                            <TextField
                                fullWidth
                                label="Arka Plan Rengi"
                                name="background_color"
                                type="color"
                                value={formData.background_color}
                                onChange={handleInputChange}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Yazı Rengi"
                                name="text_color"
                                type="color"
                                value={formData.text_color}
                                onChange={handleInputChange}
                                sx={{ mb: 2 }}
                            />
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Font</InputLabel>
                                <Select
                                    name="font_family"
                                    value={formData.font_family}
                                    onChange={handleInputChange}
                                    label="Font"
                                >
                                    {[
                                        'Arial',
                                        'Times New Roman',
                                        'Comic Sans MS',
                                        'Helvetica',
                                        'Roboto',
                                        'Courier New',
                                        'Georgia',
                                        'Verdana',
                                        'Open Sans',
                                        'Montserrat',
                                        'Ubuntu',
                                        'Source Sans Pro',
                                        'Raleway',
                                        'Poppins',
                                        'Nunito',
                                        'Lato',
                                    ].map((font) => (
                                        <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                                            {font}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Font Boyutu"
                                name="font_size"
                                type="number"
                                value={formData.font_size}
                                onChange={handleInputChange}
                                inputProps={{ min: 8, max: 72 }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Kenarlık Rengi"
                                name="border_color"
                                type="color"
                                value={formData.border_color}
                                onChange={handleInputChange}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Kenarlık Kalınlığı"
                                name="border_width"
                                type="number"
                                value={formData.border_width}
                                onChange={handleInputChange}
                                inputProps={{ min: 0, max: 10 }}
                                sx={{ mb: 2 }}
                            />
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Kenarlık Stili</InputLabel>
                                <Select
                                    name="border_style"
                                    value={formData.border_style}
                                    onChange={handleInputChange}
                                    label="Kenarlık Stili"
                                >
                                    {[
                                        'solid',
                                        'dashed',
                                        'dotted',
                                        'double',
                                        'none',
                                    ].map((style) => (
                                        <MenuItem key={style} value={style}>
                                            {style.charAt(0).toUpperCase() + style.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Köşe Yuvarlaklığı"
                                name="border_radius"
                                type="number"
                                value={formData.border_radius}
                                onChange={handleInputChange}
                                inputProps={{ min: 0, max: 50 }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="İç Boşluk"
                                name="padding"
                                type="number"
                                value={formData.padding}
                                onChange={handleInputChange}
                                inputProps={{ min: 0, max: 100 }}
                                sx={{ mb: 2 }}
                            />
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Metin Hizalama</InputLabel>
                                <Select
                                    name="alignment"
                                    value={formData.alignment}
                                    onChange={handleInputChange}
                                    label="Metin Hizalama"
                                >
                                    {[
                                        'center',
                                        'left',
                                        'right',
                                        'justify',
                                    ].map((align) => (
                                        <MenuItem key={align} value={align}>
                                            {align === 'center' ? 'Ortalı' :
                                                align === 'left' ? 'Sola Yaslı' :
                                                    align === 'right' ? 'Sağa Yaslı' :
                                                        'İki Yana Yaslı'}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>Önizleme</Typography>
                            <Box sx={{ transform: 'scale(0.8)', transformOrigin: 'top center' }}>
                                {renderPreview()}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>İptal</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {editMode ? 'Güncelle' : 'Oluştur'}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PrintIcon />}
                        onClick={handleOpenPrintPreview}
                        sx={{ ml: 1 }}
                    >
                        Yazdır / PDF Önizleme
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default InvitationDesign;